import React, { useState } from 'react';
import { Shield, Download, FileText, CheckCircle } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, NoteCard } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const PDFtoPDFA = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [complianceLevel, setComplianceLevel] = useState('PDF/A-1b');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const convertToPDFA = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);

            setProgress(30);

            // Set PDF/A metadata
            pdfDoc.setTitle(file.name.replace(/\.pdf$/i, ''));
            pdfDoc.setProducer('YoursPDF - PDF/A Converter');
            pdfDoc.setCreator('YoursPDF');
            pdfDoc.setCreationDate(new Date());
            pdfDoc.setModificationDate(new Date());

            setProgress(50);

            // Create a new PDF with PDF/A compliance markers
            const newPdf = await PDFDocument.create();

            // Copy all pages
            const pages = await newPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
            pages.forEach(page => newPdf.addPage(page));

            setProgress(70);

            // Set PDF/A metadata
            newPdf.setTitle(file.name.replace(/\.pdf$/i, '') + ' (PDF/A)');
            newPdf.setSubject(`PDF/A-compliant archival document (${complianceLevel})`);
            newPdf.setKeywords(['PDF/A', 'archival', 'long-term preservation']);
            newPdf.setProducer('YoursPDF - PDF/A Converter');
            newPdf.setCreator('YoursPDF');
            newPdf.setCreationDate(new Date());
            newPdf.setModificationDate(new Date());

            setProgress(90);

            const pdfBytes = await newPdf.save({
                useObjectStreams: false, // PDF/A requirement
                addDefaultPage: false,
                objectsPerTick: 50
            });

            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            saveAs(blob, `${fileName}_PDFA.pdf`);

            toast.success('PDF converted to PDF/A format!');
            toast.info('Metadata and structure optimized for long-term archival.');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Shield size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF to PDF/A
                </h1>
                <p className="tool-description">
                    Convert PDF documents to PDF/A archival format for long-term preservation and compliance.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPageCount(0);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Settings */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                PDF/A Compliance Level
                            </h3>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Select Compliance Level
                                </label>
                                <select
                                    value={complianceLevel}
                                    onChange={(e) => setComplianceLevel(e.target.value)}
                                    className="input"
                                >
                                    <option value="PDF/A-1b">PDF/A-1b (Basic visual preservation)</option>
                                    <option value="PDF/A-1a">PDF/A-1a (Accessible, tagged PDF)</option>
                                    <option value="PDF/A-2b">PDF/A-2b (Modern features, basic)</option>
                                    <option value="PDF/A-2a">PDF/A-2a (Modern features, accessible)</option>
                                    <option value="PDF/A-3b">PDF/A-3b (With embedded files)</option>
                                </select>
                                <p style={{
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    marginTop: 'var(--spacing-2)'
                                }}>
                                    PDF/A-1b is recommended for most archival purposes
                                </p>
                            </div>
                        </Card>
                    </div>

                    {/* Info */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-2)'
                            }}>
                                <CheckCircle size={20} color="var(--color-success)" />
                                What is PDF/A?
                            </h3>
                            <p style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.6,
                                marginBottom: 'var(--spacing-3)'
                            }}>
                                PDF/A is an ISO-standardized version of PDF specialized for digital preservation and long-term archiving.
                            </p>
                            <ul style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.6,
                                paddingLeft: 'var(--spacing-5)'
                            }}>
                                <li>Self-contained (all fonts and resources embedded)</li>
                                <li>Device-independent color spaces</li>
                                <li>No encryption or external dependencies</li>
                                <li>Metadata standards compliance</li>
                                <li>Suitable for legal and regulatory requirements</li>
                            </ul>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToPDFA}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to PDF/A
                        </Button>
                    </div>

                    {/* Info */}
                    <NoteCard variant="info">
                        <strong>Note:</strong> This tool creates a PDF/A-compliant structure with proper metadata. For full validation and certification, use specialized PDF/A validation tools or services.
                    </NoteCard>
                </>
            )}
        </div>
    );
};

export default PDFtoPDFA;
